import {
  buildScheduleWeekWorkouts,
  filterScheduleWeekWorkoutsByStatuses,
  isScheduleWeekCatalogData,
} from '~/utils/scheduleWeekView';
import { resolveDefaultSelectedWeekBounds } from '~/utils/isoWeekPicker';
import { formatWorkoutScheduleTimeDisplay } from '~/utils/trainingDeliveryFormat';
import { localPartsFromUtc, resolveDefaultTimezone, resolveTodayYmd } from '~/utils/timezone';

export const TRAINER_TASKS_PAGE_TITLE = 'Мои задачи';

/**
 * @param {{ counts?: Record<string, number>|null, weekDate?: string|null|undefined }} [options]
 */
export function buildTrainerTasksPageData(options = {}) {
  const counts = options.counts && typeof options.counts === 'object'
    ? options.counts
    : null;
  const weekDate = String(options.weekDate ?? '').slice(0, 10);
  const weekDateQuery = /^\d{4}-\d{2}-\d{2}$/.test(weekDate)
    ? { week_date: weekDate }
    : {};

  const links = TRAINER_TASKS_PRIMARY_SECTIONS.map((section) => ({
    label: section.label,
    url: section.to,
    matchPaths: section.matchPaths,
    query: weekDateQuery,
    badge: counts
      ? formatTrainerTasksBadgeCount(
        resolveTrainerTasksSectionCount(counts, section.countKey || section.countKeys),
      )
      : undefined,
  }));

  return {
    h1: TRAINER_TASKS_PAGE_TITLE,
    breadcrumbs: [
      { to: '/', label: 'Главная' },
      { to: '/tasks', label: TRAINER_TASKS_PAGE_TITLE },
    ],
    links,
    linksBelowHeader: true,
    meta: {
      title: TRAINER_TASKS_PAGE_TITLE,
      description: 'Задачи тренера по клиентам и тренировкам',
    },
  };
}

/**
 * @param {typeof TRAINER_TASKS_PREPARATION_SECTIONS|typeof TRAINER_TASKS_REVIEW_SECTIONS} sections
 * @param {string|null|undefined} weekDate
 * @param {Record<string, number>|null|undefined} [counts]
 */
function buildTrainerTasksSubNavItems(sections, weekDate, counts = null) {
  const normalizedWeekDate = String(weekDate ?? '').slice(0, 10);
  const weekDateQuery = /^\d{4}-\d{2}-\d{2}$/.test(normalizedWeekDate)
    ? { week_date: normalizedWeekDate }
    : {};

  return sections.map((section) => {
    const badge = counts
      ? formatTrainerTasksBadgeCount(resolveTrainerTasksSectionCount(counts, section.countKey))
      : undefined;

    return {
      key: section.key,
      label: section.label,
      badge,
      to: {
        path: section.to,
        query: weekDateQuery,
      },
      matchPaths: section.matchPaths,
    };
  });
}

/**
 * @param {string|null|undefined} weekDate
 * @param {Record<string, number>|null|undefined} [counts]
 */
export function buildTrainerTasksPreparationSubNavItems(weekDate, counts = null) {
  return buildTrainerTasksSubNavItems(TRAINER_TASKS_PREPARATION_SECTIONS, weekDate, counts);
}

/**
 * @param {string|null|undefined} weekDate
 * @param {Record<string, number>|null|undefined} [counts]
 */
export function buildTrainerTasksReviewSubNavItems(weekDate, counts = null) {
  return buildTrainerTasksSubNavItems(TRAINER_TASKS_REVIEW_SECTIONS, weekDate, counts);
}

export const TRAINER_TASKS_SECTION_KEYS = {
  preparation: 'preparation',
  composeWeeks: 'compose-weeks',
  prepareWorkouts: 'prepare-workouts',
  review: 'review',
  reviewIntradayMissed: 'review-intraday-missed',
  reviewNeedsAttention: 'review-needs-attention',
  reviewPrepareReport: 'review-prepare-report',
  reviewMissedWorkouts: 'review-missed-workouts',
};

export const TRAINER_TASKS_PRIMARY_SECTIONS = [
  {
    key: TRAINER_TASKS_SECTION_KEYS.preparation,
    label: 'Подготовка',
    to: '/tasks/compose-weeks',
    countKeys: ['compose_weeks', 'prepare_workouts', 'missing_program_slots'],
    matchPaths: [
      '/tasks/compose-weeks',
      '/tasks/prepare-workouts',
    ],
  },
  {
    key: TRAINER_TASKS_SECTION_KEYS.review,
    label: 'Завершённые тренировки',
    to: '/tasks/review/missed-workouts',
    countKeys: [
      'review_intraday_missed',
      'review_needs_attention',
      'review_prepare_report',
      'review_missed_workouts',
    ],
    matchPaths: [
      '/tasks/review/intraday-missed',
      '/tasks/review/needs-attention',
      '/tasks/review/prepare-report',
      '/tasks/review/missed-workouts',
    ],
  },
];

export const TRAINER_TASKS_PREPARATION_SECTIONS = [
  {
    key: TRAINER_TASKS_SECTION_KEYS.composeWeeks,
    label: 'Наполнение недель',
    to: '/tasks/compose-weeks',
    countKey: 'compose_weeks',
    matchPaths: ['/tasks/compose-weeks'],
  },
  {
    key: TRAINER_TASKS_SECTION_KEYS.prepareWorkouts,
    label: 'Подготовить тренировки',
    to: '/tasks/prepare-workouts',
    countKey: ['prepare_workouts', 'missing_program_slots'],
    matchPaths: ['/tasks/prepare-workouts'],
    statusFilter: ['draft', 'missing_slot'],
  },
];

export const TRAINER_TASKS_REVIEW_SECTIONS = [
  {
    key: TRAINER_TASKS_SECTION_KEYS.reviewMissedWorkouts,
    label: 'Пропуски',
    to: '/tasks/review/missed-workouts',
    countKey: ['review_intraday_missed', 'review_missed_workouts'],
    matchPaths: ['/tasks/review/missed-workouts', '/tasks/review/intraday-missed'],
    statusFilter: ['draft', 'ready', 'inProgress'],
  },
  {
    key: TRAINER_TASKS_SECTION_KEYS.reviewNeedsAttention,
    label: 'С замечаниями',
    to: '/tasks/review/needs-attention',
    countKey: 'review_needs_attention',
    matchPaths: ['/tasks/review/needs-attention'],
    statusFilter: ['completedNeedsAttention'],
    backlogSubstatus: 'needs_attention',
  },
  {
    key: TRAINER_TASKS_SECTION_KEYS.reviewPrepareReport,
    label: 'Не проверена',
    to: '/tasks/review/prepare-report',
    countKey: 'review_prepare_report',
    matchPaths: ['/tasks/review/prepare-report'],
    statusFilter: ['completedUnreviewed'],
    backlogSubstatus: 'prepare_report',
  },
];

/** Пояснения для информационного блока на страницах /tasks/* */
export const TRAINER_TASKS_SECTION_GUIDES = {
  [TRAINER_TASKS_SECTION_KEYS.composeWeeks]: {
    title: 'Наполнение недель',
    purpose: 'Список активных клиентов, у которых на выбранной ISO-неделе ещё не подтверждено, что тренировки добавлены в календарь.',
    steps: [
      'Выберите неделю в переключателе выше — по умолчанию открыта текущая.',
      'Откройте клиента из списка и заполните неделю в его плане тренировок.',
      'Когда состав недели готов, подтвердите добавленность тренировок в календаре клиента.',
    ],
    tips: [
      'Клиенты с полностью пропущенной (rest) неделей здесь не показываются.',
      'После изменения состава недели подтверждение сбрасывается — проверьте список снова.',
    ],
  },
  [TRAINER_TASKS_SECTION_KEYS.prepareWorkouts]: {
    title: 'Подготовить тренировки',
    purpose: 'Черновики и пустые слоты программы на выбранной неделе — всё, что нужно довести до готовности, чтобы клиент мог выполнить тренировку.',
    steps: [
      'Для пустого слота нажмите «Наполнить тренировку» или откройте план клиента на эту неделю.',
      'Для черновика откройте тренировку, заполните упражнения и нагрузки.',
      'Подтвердите сеты — тренировка станет «Подготовлена» и исчезнет из списка.',
    ],
    tips: [
      'Фильтр «Ближайшие» по умолчанию показывает сегодня и следующие два дня — снимите его, чтобы увидеть всю неделю.',
      'Пустые слоты блокируют подтверждение недели у клиента, пока их не закроете.',
      'Черновики на прошлые даты также попадут в «Пропуски», если клиент их не выполнил.',
    ],
  },
  [TRAINER_TASKS_SECTION_KEYS.reviewMissedWorkouts]: {
    title: 'Пропуски',
    purpose: 'Незавершённые и просроченные тренировки: сегодняшние сессии с прошедшим временем начала и пропуски прошлых дней на выбранной неделе.',
    steps: [
      'Начните с блока «Сегодня» — там сессии, которые клиент уже должен был начать.',
      'Разберите пропуски прошлых дней: связаться с клиентом, перенести или отменить тренировку.',
      'Если тренировка всё же была — завершите её и внесите факт.',
    ],
    tips: [
      '«Сегодня» обновляется по мере прохождения времени начала — проверяйте блок в течение дня.',
      '«Ранее этой недели» — backlog до начала выбранной ISO-недели.',
      '«На этой неделе» — пропуски только внутри выбранной недели.',
    ],
  },
  [TRAINER_TASKS_SECTION_KEYS.reviewNeedsAttention]: {
    title: 'С замечаниями',
    purpose: 'Завершённые тренировки с замечаниями или нестандартным выполнением — нужно просмотреть анализ и подтвердить его.',
    steps: [
      'Откройте тренировку из списка.',
      'Просмотрите замечания и результаты упражнений.',
      'Подтвердите проверку — тренировка перейдёт в обработанные.',
    ],
    tips: [
      'Блок «Ранее этой недели» показывает просроченные задачи с прошлых недель.',
      'Сначала закройте этот список — клиент ждёт обратной связь по факту выполнения.',
    ],
  },
  [TRAINER_TASKS_SECTION_KEYS.reviewPrepareReport]: {
    title: 'Не проверена',
    purpose: 'Завершённые тренировки, которые вы ещё не проверили.',
    steps: [
      'Откройте тренировку и перейдите в режим «Проверка».',
      'Сверьте факт выполнения и при необходимости добавьте комментарии.',
      'Подтвердите проверку — тренировка исчезнет из списка.',
    ],
    tips: [
      'Просроченные задачи с прошлых недель собраны в отдельном блоке сверху.',
      'Если добавили комментарии, клиент получит их после подтверждения.',
    ],
  },
};

/**
 * @param {string|null|undefined} sectionKey
 */
export function resolveTrainerTasksSectionGuide(sectionKey) {
  const key = String(sectionKey ?? '').trim();
  return TRAINER_TASKS_SECTION_GUIDES[key] ?? null;
}

/**
 * @param {string|null|undefined} path
 */
export function resolveTrainerTasksSectionGuideByPath(path) {
  const normalized = String(path || '').replace(/\/+$/, '') || '/';
  const allSections = [
    ...TRAINER_TASKS_PREPARATION_SECTIONS,
    ...TRAINER_TASKS_REVIEW_SECTIONS,
  ];
  const match = allSections.find((section) =>
    section.matchPaths.some((entry) => normalized === entry || normalized.startsWith(`${entry}/`)),
  );

  return match ? resolveTrainerTasksSectionGuide(match.key) : null;
}

/** Приоритет умного редиректа с /tasks */
export const TRAINER_TASKS_SMART_REDIRECT_ORDER = [
  {
    path: '/tasks/review/missed-workouts',
    countKey: ['review_intraday_missed', 'review_missed_workouts'],
  },
  {
    path: '/tasks/prepare-workouts',
    countKey: ['prepare_workouts', 'missing_program_slots'],
  },
  {
    path: '/tasks/review/needs-attention',
    countKey: 'review_needs_attention',
  },
  {
    path: '/tasks/compose-weeks',
    countKey: 'compose_weeks',
  },
  {
    path: '/tasks/review/prepare-report',
    countKey: 'review_prepare_report',
  },
];

export const TRAINER_TASKS_PREPARE_STATUSES = ['draft'];
export const TRAINER_TASKS_FILL_SLOT_STATUSES = ['missing_slot'];
export const TRAINER_TASKS_MISSED_STATUSES = ['draft', 'ready', 'inProgress'];
export const TRAINER_TASKS_UPCOMING_DAYS = 2;

const EMPTY_COUNTS = {
  compose_weeks: 0,
  prepare_workouts: 0,
  prepare_workouts_upcoming: 0,
  missing_program_slots: 0,
  schedule_overlaps: 0,
  review_intraday_missed: 0,
  review_needs_attention: 0,
  review_prepare_report: 0,
  review_missed_workouts: 0,
  pending_invites: 0,
  review_backlog: 0,
  daily_priority_total: 0,
  total_sidebar: 0,
};

/**
 * @param {Record<string, number>|null|undefined} counts
 * @param {string|string[]} countKey
 */
export function resolveTrainerTasksSectionCount(counts, countKey) {
  if (!counts || typeof counts !== 'object') {
    return 0;
  }

  if (Array.isArray(countKey)) {
    return countKey.reduce(
      (sum, key) => sum + Math.max(0, Number(counts[key] ?? 0)),
      0,
    );
  }

  return Math.max(0, Number(counts[countKey] ?? 0));
}

/**
 * @param {Record<string, number>|null|undefined} counts
 */
export function resolveTrainerTasksSmartRedirectPath(counts) {
  for (const entry of TRAINER_TASKS_SMART_REDIRECT_ORDER) {
    if (resolveTrainerTasksSectionCount(counts, entry.countKey) > 0) {
      return entry.path;
    }
  }

  return '/tasks/compose-weeks';
}

/**
 * @param {string} path
 */
export function isTrainerTasksReviewPath(path) {
  return String(path || '').startsWith('/tasks/review/');
}

/**
 * @param {string} path
 */
export function isTrainerTasksPreparationPath(path) {
  const normalized = String(path || '').replace(/\/+$/, '') || '/';
  return TRAINER_TASKS_PREPARATION_SECTIONS.some((section) =>
    section.matchPaths.some((entry) => normalized === entry || normalized.startsWith(`${entry}/`)),
  );
}

/**
 * @param {string} path
 */
export function resolveTrainerTasksPrimaryActiveKey(path) {
  const normalized = String(path || '').replace(/\/+$/, '') || '/';
  const match = TRAINER_TASKS_PRIMARY_SECTIONS.find((section) =>
    section.matchPaths.some((entry) => normalized === entry || normalized.startsWith(`${entry}/`)),
  );
  return match?.key ?? TRAINER_TASKS_SECTION_KEYS.preparation;
}

/**
 * @param {string} path
 */
export function resolveTrainerTasksPreparationActiveKey(path) {
  const normalized = String(path || '').replace(/\/+$/, '') || '/';
  const match = TRAINER_TASKS_PREPARATION_SECTIONS.find((section) =>
    section.matchPaths.some((entry) => normalized === entry),
  );
  return match?.key ?? TRAINER_TASKS_SECTION_KEYS.composeWeeks;
}

/**
 * @param {string} path
 */
export function resolveTrainerTasksReviewActiveKey(path) {
  const normalized = String(path || '').replace(/\/+$/, '') || '/';
  const match = TRAINER_TASKS_REVIEW_SECTIONS.find((section) =>
    section.matchPaths.some((entry) => normalized === entry),
  );
  return match?.key ?? TRAINER_TASKS_SECTION_KEYS.reviewMissedWorkouts;
}

/**
 * @param {Array<{ date?: string, workouts?: unknown[] }>} catalogDays
 * @param {string[]} selectedStatuses
 */
export function filterTrainerTasksWeekWorkouts(catalogDays, selectedStatuses) {
  const workouts = buildScheduleWeekWorkouts(catalogDays);
  return filterScheduleWeekWorkoutsByStatuses(workouts, selectedStatuses);
}

/**
 * @param {Array<{ date?: string, workouts?: unknown[] }>} catalogDays
 */
export function filterTrainerTasksMissingSlotWorkouts(catalogDays) {
  const workouts = buildScheduleWeekWorkouts(catalogDays);
  return (workouts || []).filter((workout) => (
    workout?.status === 'missing_slot' || Boolean(workout?.is_missing_slot)
  ));
}

/**
 * Черновики и пустые слоты программы — единый список подготовки тренировок.
 * Пустые слоты входят в фильтр «draft» через resolveWorkoutOverviewStatusFilterVariant.
 *
 * @param {Array<{ date?: string, workouts?: unknown[] }>} catalogDays
 */
export function filterTrainerTasksPrepareWorkouts(catalogDays) {
  return filterTrainerTasksWeekWorkouts(catalogDays, TRAINER_TASKS_PREPARE_STATUSES);
}

/** Счётчик срочных задач подготовки: ближайшие черновики + все пустые слоты. */
export const TRAINER_TASKS_PREPARE_URGENT_COUNT_KEYS = [
  'prepare_workouts_upcoming',
  'missing_program_slots',
];

/** Полный счётчик задач подготовки на неделе. */
export const TRAINER_TASKS_PREPARE_COUNT_KEYS = [
  'prepare_workouts',
  'missing_program_slots',
];

function resolveRawWorkoutTrainingDateYmd(raw) {
  const value = raw?.training_date;
  if (!value) {
    return '';
  }
  if (typeof value === 'object' && value.val) {
    return String(value.val).slice(0, 10);
  }
  return String(value).slice(0, 10);
}

/**
 * @param {Array<Record<string, unknown>>} rawWorkouts
 * @param {string|null|undefined} [fallbackDateYmd]
 */
export function buildTrainerTasksWorkoutsFromRawList(rawWorkouts, fallbackDateYmd = '') {
  if (!Array.isArray(rawWorkouts) || rawWorkouts.length === 0) {
    return [];
  }

  const fallback = String(fallbackDateYmd ?? '').slice(0, 10);
  const hasFallback = /^\d{4}-\d{2}-\d{2}$/.test(fallback);
  const workouts = hasFallback
    ? rawWorkouts.map((raw) => {
      if (!raw || typeof raw !== 'object') {
        return raw;
      }

      const trainingDate = resolveRawWorkoutTrainingDateYmd(raw);
      const effectiveDate = String(raw?.schedule_context?.effective_date ?? '').slice(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(trainingDate) || /^\d{4}-\d{2}-\d{2}$/.test(effectiveDate)) {
        return raw;
      }

      return { ...raw, training_date: fallback };
    })
    : rawWorkouts;

  return buildScheduleWeekWorkouts([
    {
      date: hasFallback ? fallback : '',
      workouts,
      is_undated: false,
    },
  ]);
}

/**
 * @param {string} weekStartYmd
 * @param {number} upcomingDays
 * @returns {{ startYmd: string, endYmd: string }}
 */
export function resolveTrainerTasksUpcomingFilterBounds(
  weekStartYmd,
  upcomingDays = TRAINER_TASKS_UPCOMING_DAYS,
) {
  const todayYmd = new Date().toISOString().slice(0, 10);
  const start = todayYmd >= weekStartYmd ? todayYmd : weekStartYmd;
  const endDate = new Date(`${start}T00:00:00.000Z`);
  endDate.setUTCDate(endDate.getUTCDate() + Math.max(0, upcomingDays));
  const endYmd = endDate.toISOString().slice(0, 10);

  return { startYmd: start, endYmd };
}

/** Число календарных дней в окне «ближайшие» (включая первый день). */
export function resolveTrainerTasksUpcomingFilterDayCount(
  upcomingDays = TRAINER_TASKS_UPCOMING_DAYS,
) {
  return Math.max(0, upcomingDays) + 1;
}

/** «3 дня», «1 день», «5 дней» — для подписи фильтра. */
export function formatTrainerTasksUpcomingFilterDayLabel(
  upcomingDays = TRAINER_TASKS_UPCOMING_DAYS,
) {
  const count = resolveTrainerTasksUpcomingFilterDayCount(upcomingDays);
  const mod10 = count % 10;
  const mod100 = count % 100;
  let word = 'дней';

  if (mod10 === 1 && mod100 !== 11) {
    word = 'день';
  } else if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) {
    word = 'дня';
  }

  return `${count} ${word}`;
}

/**
 * @param {Array<{ date_sort?: string, training_date?: unknown }>} workouts
 * @param {string} weekStartYmd
 * @param {number} upcomingDays
 */
export function filterTrainerTasksUpcomingWorkouts(workouts, weekStartYmd, upcomingDays = TRAINER_TASKS_UPCOMING_DAYS) {
  const { startYmd: start, endYmd } = resolveTrainerTasksUpcomingFilterBounds(
    weekStartYmd,
    upcomingDays,
  );

  return (workouts || []).filter((workout) => {
    const dateYmd = String(workout?.date_sort ?? workout?.training_date ?? '').slice(0, 10);
    if (!dateYmd) {
      return true;
    }
    return dateYmd >= start && dateYmd <= endYmd;
  });
}

export function resolveTrainerTasksTodayYmd(viewerTimezone) {
  return resolveTodayYmd(viewerTimezone ?? resolveDefaultTimezone());
}

/**
 * @param {Array<{ date_sort?: string, training_date?: unknown }>} workouts
 * @param {string|null|undefined} [todayYmd]
 */
export function filterTrainerTasksMissedWorkouts(workouts, todayYmd = resolveTrainerTasksTodayYmd()) {
  const today = String(todayYmd ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(today)) {
    return workouts || [];
  }

  return (workouts || []).filter((workout) => {
    const dateYmd = String(workout?.date_sort ?? workout?.training_date ?? '').slice(0, 10);
    if (!dateYmd) {
      return false;
    }
    return dateYmd < today;
  });
}

/**
 * @param {string|null|undefined} timeRaw
 */
export function normalizeTrainerTasksTimeHm(timeRaw) {
  const value = String(timeRaw ?? '').trim();
  if (!value || value === '--:--') {
    return null;
  }
  if (/^\d{2}:\d{2}$/.test(value)) {
    return `${value}:00`;
  }
  if (/^\d{2}:\d{2}:\d{2}$/.test(value)) {
    return value;
  }
  return null;
}

/**
 * @param {string|null|undefined} [nowTimeHm]
 * @param {string|null|undefined} [viewerTimezone]
 */
export function resolveTrainerTasksNowTimeHm(nowTimeHm, viewerTimezone) {
  if (normalizeTrainerTasksTimeHm(nowTimeHm)) {
    return normalizeTrainerTasksTimeHm(nowTimeHm);
  }

  const tz = viewerTimezone ?? resolveDefaultTimezone();
  const parts = localPartsFromUtc(new Date().toISOString(), tz);
  if (parts?.time) {
    const [hours, minutes] = parts.time.split(':');
    return `${hours}:${minutes}:00`;
  }

  const now = new Date();
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}:00`;
}

/**
 * @param {Record<string, unknown>|null|undefined} workout
 * @param {string|null|undefined} [todayYmd]
 * @param {string|null|undefined} [nowTimeHm]
 * @param {string|null|undefined} [viewerTimezone]
 */
export function isTrainerTasksIntradayOverdueWorkout(
  workout,
  todayYmd = resolveTrainerTasksTodayYmd(),
  nowTimeHm = resolveTrainerTasksNowTimeHm(undefined, undefined),
  viewerTimezone,
) {
  const today = String(todayYmd ?? resolveTrainerTasksTodayYmd(viewerTimezone)).slice(0, 10);
  const dateYmd = String(
    workout?.schedule_context?.timezone_context?.viewer?.local_date
    ?? workout?.date_sort
    ?? workout?.training_date
    ?? '',
  ).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(today) || dateYmd !== today) {
    return false;
  }

  if (workout?.ent_is_completed || workout?.workout_status === 'cancelled' || workout?.cancel_reason) {
    return false;
  }

  if (workout?.is_intraday_overdue) {
    return true;
  }

  const startHm = normalizeTrainerTasksTimeHm(
    workout?.schedule_context?.timezone_context?.viewer?.local_time
    || formatWorkoutScheduleTimeDisplay(workout)
    || workout?.start_time,
  );
  const nowHm = resolveTrainerTasksNowTimeHm(nowTimeHm, viewerTimezone);
  if (!startHm || !nowHm) {
    return false;
  }

  return startHm < nowHm;
}

/**
 * @param {Array<Record<string, unknown>>} workouts
 * @param {string|null|undefined} [todayYmd]
 * @param {string|null|undefined} [nowTimeHm]
 */
export function filterTrainerTasksIntradayMissedWorkouts(
  workouts,
  todayYmd = resolveTrainerTasksTodayYmd(),
  nowTimeHm = resolveTrainerTasksNowTimeHm(undefined, undefined),
  viewerTimezone,
) {
  return (workouts || []).filter((workout) => (
    isTrainerTasksIntradayOverdueWorkout(workout, todayYmd, nowTimeHm, viewerTimezone)
  ));
}

/**
 * @param {Record<string, number>|null|undefined} counts
 * @param {number} [unreadTotal]
 */
export function resolveTrainerTasksDailyPriorityTotal(counts, unreadTotal = 0) {
  const normalized = normalizeTrainerTasksCounts(counts);
  return Math.max(0, Number(normalized.daily_priority_total ?? 0))
    + Math.max(0, Number(unreadTotal ?? 0));
}

/**
 * @param {Record<string, unknown>|null|undefined} raw
 */
export function normalizeTrainerTasksCounts(raw) {
  if (!raw || typeof raw !== 'object') {
    return { ...EMPTY_COUNTS };
  }

  return {
    compose_weeks: Math.max(0, Number(raw.compose_weeks ?? 0)),
    prepare_workouts: Math.max(0, Number(raw.prepare_workouts ?? 0)),
    prepare_workouts_upcoming: Math.max(0, Number(raw.prepare_workouts_upcoming ?? 0)),
    missing_program_slots: Math.max(0, Number(raw.missing_program_slots ?? 0)),
    schedule_overlaps: Math.max(0, Number(raw.schedule_overlaps ?? 0)),
    review_intraday_missed: Math.max(0, Number(raw.review_intraday_missed ?? 0)),
    review_needs_attention: Math.max(0, Number(raw.review_needs_attention ?? 0)),
    review_prepare_report: Math.max(0, Number(raw.review_prepare_report ?? 0)),
    review_missed_workouts: Math.max(0, Number(raw.review_missed_workouts ?? 0)),
    pending_invites: Math.max(0, Number(raw.pending_invites ?? 0)),
    review_backlog: Math.max(0, Number(raw.review_backlog ?? 0)),
    daily_priority_total: Math.max(0, Number(raw.daily_priority_total ?? 0)),
    total_sidebar: Math.max(0, Number(raw.total_sidebar ?? 0)),
    iso_year: Number(raw.iso_year ?? 0) || null,
    iso_week: Number(raw.iso_week ?? 0) || null,
    week_start: String(raw.week_start ?? '').slice(0, 10) || null,
  };
}

export function resolveTrainerTasksSidebarWeekDate() {
  return resolveDefaultSelectedWeekBounds()?.dateStart || new Date().toISOString().slice(0, 10);
}

export function formatTrainerTasksBadgeCount(count) {
  const value = Math.max(0, Number(count ?? 0));
  if (value <= 0) {
    return undefined;
  }
  return value > 99 ? '99+' : value;
}

export { EMPTY_COUNTS };
